import { BCMSDate, BCMSDefaultModel, BCMSStateManagerCRUD, BCMSRecord, DisabledPrompt, useBCMSContext, useBCMSModal, useRoute, useStateManager,useWorkflow } from "bcms-react";
import Classes, { Account, ACHTemplate, ACHFromAccount, ACHToParticipant, CompanyAccount, Participant } from "model";
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from 'components/shared';
import { FormButtonRow } from 'components/shared/containers/FormContainer';
import { PrimaryButton, SecondaryButton, UICheckbox, UITextInput } from 'components/ui';
import React, { useEffect } from 'react';
import "./mobile.css";
import "./styles.css";
import ACHTransfersFromSection from "components/shared/widgets/ACHWidgets/ACHTransfersFromSection";
import TemplateDetailsToSection from "./components/TemplateDetailsToSection";
import { SVGAsset } from "res";
import { ConfirmBannerView } from "components/shared";

/**
 * @function TemplateDetailsPage
 * @return {React.Component}
 * @exported true
 * @description The page that shows the template details
 */


export default function TemplateDetailsPage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };
    const navigate = useRoute("");

    const workflow = useWorkflow();
    const hasWorkflow = workflow ? true : false;
    const { nextStep } = useWorkflow() ?? {};

    // templateNameAlreadyExistsGlobally :: (React.state) -> BOOLEAN
    // Returns true if the current template name already exists globally.
    // Do not create a template if the same template name already exists globally.
    const templateNameAlreadyExistsGlobally = state => {
        const template = state.data.template;
        const defaultTemplate = BCMSDefaultModel.achTemplate();
        let exists = false;
        ACHTemplate.initAll().forEach(item => {
            if(item.name === template.name) exists = true;
            if((template.name === undefined || template.name === "") && item.name === defaultTemplate.name) {
                exists = true;
            }
        });
        return exists;
    }
    
    // createBCMSRecordTemplate :: (React.state, React.setState) -> VOID
    // Globally creates an ACH Template and associated ACHFromAccounts and ACHToParticipants.
    const createBCMSRecordTemplate = (state, setState) => {
        const template = state.data.template;
        const defaultTemplate = BCMSDefaultModel.achTemplate();
        const achFromAccountIDs = state.data.achFromAccounts.accounts.reduce((acc, item) => {
            acc.push(`${ item.id }`)
            return acc;
        }, []);
        const achToParticipantsIDs = state.data.achToParticipants.participants.reduce((acc, item) => {
            acc.push(`${ item.id }`)
            return acc;
        }, []);

        const mergedTemplateRecord = BCMSDefaultModel.mergeRecords(template.record, defaultTemplate.record);
        mergedTemplateRecord.achFromAccounts = achFromAccountIDs.length > 0 ? achFromAccountIDs : undefined;
        mergedTemplateRecord.achToParticipants = achToParticipantsIDs.length > 0 ? achToParticipantsIDs : undefined;
        
        const workflowDefaultTemplate = getDefaultData().template;
        const nextTemplateID = `${ parseFloat(state.data.template.id) + 1 }`;
        workflowDefaultTemplate.id = nextTemplateID;
        workflowDefaultTemplate.name = mergedTemplateRecord.name;

        const achFromAccountUpdates = state.data.achFromAccounts.accounts.reduce((acc, item) => {
            acc.push(ACHFromAccount.create({ ...item.record, achTemplate: state.data.template.id }));
            return acc;
        }, []);

        const debitAmount = state.data.achFromAccounts.accounts.reduce((acc, item) => {
            if(item.amount) {
                acc = acc + (item.amount.amount ? parseFloat(item.amount.amount) : 0);
            }
            return acc;
        }, 0);

        const creditAmount = state.data.achToParticipants.participants.reduce((acc, item) => {
            if(item.amount && !item.hold) {
                acc = acc + (item.amount.amount ? parseFloat(item.amount.amount) : 0);
            }
            return acc;
        }, 0);

        mergedTemplateRecord.debitAmount = debitAmount;
        mergedTemplateRecord.creditAmount = creditAmount;

        const achToParticipantsUpdates = state.data.achToParticipants.participants.reduce((acc, item) => {
            acc.push(ACHToParticipant.create({ ...item.record, achTemplate: state.data.template.id }));
            return acc;
        }, []);
        
        BCMSRecord.batch([
            ...achFromAccountUpdates,
            ...achToParticipantsUpdates,
            ACHTemplate.create(mergedTemplateRecord)
        ], context, Classes);

        const crud = BCMSStateManagerCRUD.init(state);

        if(state.data.achFromAccounts.accounts.length > 0) {
            let lastACHFromAccountID = state.data.achFromAccounts.accounts[0].id;
            const updatedAchFromAccounts = state.data.achFromAccounts.accounts.reduce((acc, item) => {
                lastACHFromAccountID = lastACHFromAccountID + 1;
                item.id = lastACHFromAccountID;
                acc.push(item);
                return acc;
            }, []);
            
            crud.update([ "data", "achFromAccounts", "accounts" ], updatedAchFromAccounts);
        }

        if(state.data.achToParticipants.participants.length > 0) {
            let lastACHToParticipantID = state.data.achToParticipants.participants[0].id;
            const updatedAchToParticipants = state.data.achToParticipants.participants.reduce((acc, item) => {
                lastACHToParticipantID = lastACHToParticipantID + 1;
                item.id = lastACHToParticipantID;
                acc.push(item);
                return acc;
            }, []);
            
            crud.update([ "data", "achToParticipants", "participants" ], updatedAchToParticipants);
        }

        crud.update([ "data", "template" ], workflowDefaultTemplate);
        crud.submit(setState);
    }
    
    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onChangeUIProp: prop => val => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", prop, "isActive" ], val)
            .submit(setState);
        },

        onChangeDataProp: prop => val => {
            const crud = BCMSStateManagerCRUD.init(state);

            if(prop === "schedule" && (val === "Immediate" || val === "Future Dated")) {
                crud.update([ "data", "template", "effectiveDate" ], BCMSDate.init().nextBusinessDay());
            } else if(prop === "schedule" && val === "Same-Day") {
                crud.update([ "data", "template", "effectiveDate" ], BCMSDate.init());
            }

            if(prop === "offsetAccount") {
                const selectedOffsetAccount = Account.initByID(val);
                crud.update([ "data", "selectedOffsetAccount", "account" ], selectedOffsetAccount);
            } else {
                crud.update([ "data", "template", prop ], val);
            }
            
            crud.submit(setState);
        },

        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", prop], value)
                .submit(setState);
        },

        onTemplateChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "template", prop], value)
                .submit(setState);
        },

        onACHFromAccountChange: (achFromAccount) => val => {
            const findIndex = data.achFromAccounts.accounts.findIndex(item => item.id === achFromAccount.id);
            const updateAccount = ACHFromAccount.init({ ...achFromAccount.record, amount: val.amount });
            const achFromAccounts = [
                ...data.achFromAccounts.accounts.slice(0, findIndex),
                updateAccount,
                ...data.achFromAccounts.accounts.slice(findIndex + 1)
            ];

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achFromAccounts", "accounts" ], achFromAccounts)
            .submit(setState);
        },

        onACHFromAccountDelete: (achFromAccount) => val => {
            const achFromAccounts = data.achFromAccounts.accounts.filter(item => item.id !== achFromAccount.id);
            const updatedOffsetAccounts = getDefaultData().offsetAccounts.filter(item => {
                const currIDs = achFromAccounts.reduce((acc, _item) => {
                    acc.push(_item.account.id);
                    return acc;
                }, []);
                return !currIDs.includes(item.id);
            });

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "offsetAccounts" ], updatedOffsetAccounts)
            .update([ "data", "achFromAccounts", "accounts" ], achFromAccounts)
            .submit(setState);
        },

        onAddOffsetAccountClick: () => {
            if(!data.selectedOffsetAccount.account) return;
            let achFromAccounts = data.achFromAccounts.accounts;
            const selectedOffsetAccount = data.selectedOffsetAccount.account;
            let nextID = undefined;
            if(achFromAccounts.length === 0) {
                // check ids globally
                nextID = ACHFromAccount.nextID();
            } else {
                nextID = parseFloat(achFromAccounts[achFromAccounts.length - 1].id) + 1;
            }
        
            const addedAccount = ACHFromAccount.init({ account: selectedOffsetAccount.id, id: nextID });
            achFromAccounts = [ addedAccount, ...achFromAccounts ];
            const updatedOffsetAccounts = getDefaultData().offsetAccounts.filter(item => {
                const currIDs = achFromAccounts.reduce((acc, _item) => {
                    acc.push(_item.account.id);
                    return acc;
                }, []);
                return !currIDs.includes(item.id);
            });

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "offsetAccounts" ], updatedOffsetAccounts)
            .update([ "data", "achFromAccounts", "accounts" ], achFromAccounts)
            .update([ "data", "selectedOffsetAccount", "account" ], undefined)
            .submit(setState);
        },

        onParticipantModalCancelClick: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], false)
            .submit(setState);
        },

        onParticipantsModalAddSelectedParticipantsClick: (participantsInModal) => {
            let achToParticipants = state.data.achToParticipants.participants;
            let nextID = undefined;
            if(achToParticipants.length === 0) {
                // check ids globally
                nextID = ACHToParticipant.nextID();
            } else {
                nextID = parseFloat(achToParticipants[achToParticipants.length - 1].id) + 1;
            }

            const existingAchToParticipantIDs = data.achToParticipants.participants.reduce((acc, item) => {
                acc.push(item.id);
                return acc;
            }, []);

            if(existingAchToParticipantIDs.includes(nextID)) {
                nextID += 1;
            }

            const updatedACHToParticipants = participantsInModal.reduce((acc, item) => {
                const added = ACHToParticipant.init({ 
                    id: nextID, participant: item.id,
                    oneTimeACHPayment: data.oneTimeACHPayment,
                    notify: "No", addenda: "0", numberOfDistributions: "2" });
                nextID = parseFloat(nextID) + 1;
                acc.push(added);
                return acc;
            }, [])

            achToParticipants = [ ...updatedACHToParticipants, ...achToParticipants ];

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achToParticipants", "participants" ], achToParticipants)
            .update([ "ui", "showSavedTemplateBanner", "isActive" ], false)
            .update([ "ui", "showTemplateNameAlreadyExistsBanner", "isActive" ], false)
            .update([ "ui", "showUnsavedChangesBanner", "isActive" ], true)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], false)
            .submit(setState);
        },

        onACHToParticipantChange: (prop, achToParticipant) => val => {
            const findIndex = data.achToParticipants.participants.findIndex(item => item.id === achToParticipant.id);
            const record = { ...achToParticipant.record };
            record[prop] = val;
            const updateACHToParticipant = ACHToParticipant.init(record);
            const achToParticipants = [
                ...data.achToParticipants.participants.slice(0, findIndex),
                updateACHToParticipant,
                ...data.achToParticipants.participants.slice(findIndex + 1)
            ];

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achToParticipants", "participants" ], achToParticipants)
            .submit(setState);
        },

        onAddParticipantsOpenModalClick: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], true)
            .submit(setState);
        },

        onACHToParticipantDelete: (achToParticipant) => val => {
            const achToParticipants = state.data.achToParticipants.participants.filter(item => item.id !== achToParticipant.id);

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "achToParticipants", "participants" ], achToParticipants)
            .update([ "ui", "achParticipantsModalIsOpen", "isActive" ], false)
            .submit(setState);
        },

        onSaveButtonClick: () => {
            const templateName = state.data.template.name ? state.data.template.name : BCMSDefaultModel.achTemplate().name;

            if(templateNameAlreadyExistsGlobally(state)) {
                BCMSStateManagerCRUD.init(state)
                .update([ "ui", "showSavedTemplateBanner", "isActive" ], false)
                .update([ "ui", "showUnsavedChangesBanner", "isActive" ], false)
                .update([ "ui", "createdTemplateName" ], templateName)
                .update([ "ui", "showTemplateNameAlreadyExistsBanner", "isActive" ], true)
                .submit(setState);
            } else {

                if(ui.workflowType !== "nonNachaFileImportWorkflow"){
                    createBCMSRecordTemplate(state, setState);
                }
   

                BCMSStateManagerCRUD.init(state)
                .update([ "ui", "showUnsavedChangesBanner", "isActive" ], false)
                .update([ "ui", "showTemplateNameAlreadyExistsBanner", "isActive" ], false)
                .update([ "ui", "createdTemplateName" ], templateName)
                .update([ "ui", "showSavedTemplateBanner", "isActive" ], true)
                .submit(setState);
            }

            window.scrollTo(0, 0);
        },

        onSaveAndCloseButtonClick: () => {
            const templateName = state.data.template.name ? state.data.template.name : BCMSDefaultModel.achTemplate().name;

            if(templateNameAlreadyExistsGlobally(state)) {
                BCMSStateManagerCRUD.init(state)
                .update([ "ui", "showSavedTemplateBanner", "isActive" ], false)
                .update([ "ui", "showUnsavedChangesBanner", "isActive" ], false)
                .update([ "ui", "createdTemplateName" ], templateName)
                .update([ "ui", "showTemplateNameAlreadyExistsBanner", "isActive" ], true)
                .submit(setState);

                window.scrollTo(0, 0);
            } else {
       
                if(ui.workflowType === "nonNachaFileImportWorkflow"){

                    const achToParticipantsIDs = state.data.achToParticipants.participants.reduce((acc, item) => {
                        acc.push(`${ item.id }`)
                        return acc;
                    }, []);

                    const achToParticipants = achToParticipantsIDs.length > 0 ? achToParticipantsIDs : [];
                    const template = ACHTemplate.init({...state.data.template.record, achToParticipants: achToParticipants })


                    nextStep({ 
                        data: { ...state.data, createdTemplate:state.data.template,  achTemplates: [template]},
                        ui:{ ...state.ui, showCreatedACHTemplateBanner: true, searchOptions: { show: false }, addTemplateButton: { show: false } }
                    });
                }else{
                    createBCMSRecordTemplate(state, setState);
                    navigate("/achTemplates", { state: { showACHTemplateCreatedBanner: true, achTemplate: state.data.template }});
                }
            }
        }
    });

    return (
        <PageContainer className="template-details-page" state={{ data, ui }} controller={controller}>
            <PageContent />
        </PageContainer>
    );
}

export const getDefaultData = () => ({
    template: ACHTemplate.init({ 
        id: ACHTemplate.nextID(),
        denyAccessToUserIDs: []
    }),
    companyAccounts: CompanyAccount.initAll(),
    offsetAccounts: Account.initAllBusiness(),
    achFromAccounts: {
        accounts: [] // always empty by default since we are making a new payment.
    }, 
    achToParticipants: {
        participants: [] // always empty by default since we are making a new payment.
    }, 
    participants: Participant.initAll().map(item => {
        // "isSelected" will be used to keep track of row selection.
        item.isSelected = undefined;
        return item;
    }),
    groupSearchValue: "",
    selectedOffsetAccount: { account: undefined }
});

export const getDefaultUI = () => ({
    createdTemplateName: BCMSDefaultModel.achTemplate().name,
    showOneTimeACHModal: { isActive: true },
    achParticipantsModalIsOpen: { isActive: false },
    showSecCode: { isActive: false },
    showUnsavedChangesBanner: { isActive: false },
    showSavedTemplateBanner: { isActive: false },
    showTemplateNameAlreadyExistsBanner: { isActive: false },
    companySelectIsActive: { isActive: false },
    offsetAccountSelectIsActive: { isActive: false },
    transactionTypeSelectIsActive: { isActive: false },
    secCodeSelectIsActive: { isActive: false },
    showOffsetInvalidBanner: {isActive: false},
    workflowType: "",
});

function PageContent(props) {
    const { data, ui, onChangeDataProp, onChangeUIProp, onChange, onTemplateChange, onAddOffsetAccountClick, 
        onACHFromAccountChange, onACHFromAccountDelete, onParticipantRowChange, onParticipantModalCancelClick, 
        onParticipantsModalAddSelectedParticipantsClick, onACHToParticipantChange, onACHToParticipantDelete,
        onSelectAllParticipantsCheckboxChange, onAddParticipantsOpenModalClick, onSaveButtonClick, onSaveAndCloseButtonClick } = useStateManager();
    const template = data.template;

    const navigate = useRoute();
    const { close, open } = useBCMSModal();
    
    const companyAccountItems = data.companyAccounts.reduce((acc, account) => {
        acc.push(<CustomSelectItem key={account.id} value={account.id} dataPoint={`Template_Details_Company_Account_${account.id}`}>{`${account.nickname} - ${account.number}`}</CustomSelectItem>);
        return acc;
    }, []);

        // An object that contains the functions for the page controller.
        const action = { action: {
                onChangeDataProp, onChangeUIProp, onAddOffsetAccountClick, 
                onACHFromAccountChange, onACHFromAccountDelete, onParticipantRowChange, onParticipantModalCancelClick, 
                onParticipantsModalAddSelectedParticipantsClick, onACHToParticipantChange, onACHToParticipantDelete,
                onSelectAllParticipantsCheckboxChange, onAddParticipantsOpenModalClick
            } 
        };

    const propChangesToListenTo = [ JSON.stringify(template) ];
    const secCodes = props.secCodes ?? [
        { id: "0", value: "PPD Credit - Consumer Credit" },
        { id: "1", value: "CCD Credit - Non-Consumer Credit", disabled: true },
        { id: "2", value: "Tax Payments", disabled: true },
        { id: "3", value: "CTX Credit - Corporate Trade Exchange Credit", disabled: true },
        { id: "4", value: "Child Support Payments", disabled: true },
    ];


    useEffect(() => {
        if(ui.showOneTimeACHModal.isActive) {
            open("OneTimeACHModal", { 
                ui,
                data,
                secCodes: secCodes, 
                onChangeDataProp,
                onChangeUIProp,
                onCloseClickNavigateTo: "/achTemplates",
                oneTimeACHPayment: template
            });
        } 
    }, propChangesToListenTo);

    return (
        <PageContentView>
            <CenterContentContainer>
                { ui.showUnsavedChangesBanner.isActive && 
                <ConfirmBannerView text={ `Note: There are unsaved changes. If you navigate from this screen without clicking Save, the changes will be lost.`} icon={<SVGAsset id="InfoIcon" className="set-width icon checkmark-icon unsaved-changes" />} />
                }
                { ui.showSavedTemplateBanner.isActive && 
                <ConfirmBannerView text={ `The "${ ui.createdTemplateName }" template has been added.`} icon={<SVGAsset id="InfoIcon" className="set-width icon checkmark-icon saved-changes" />} />
                }
                { ui.showTemplateNameAlreadyExistsBanner.isActive && 
                <ConfirmBannerView text={ `There is already a template named "${ ui.createdTemplateName }".`} icon={<SVGAsset id="AlertTriangleIcon" className="set-width icon checkmark-icon error-prompt-icon" />} />
                }
                { ui.showOffsetInvalidBanner.isActive && 
                <ConfirmBannerView text={ `Batch #${data.template.name}: The "${data.template.name}" batch is UNBALANCE because the offset account is invalid.`} icon={<SVGAsset id="InfoIcon" className="set-width icon checkmark-icon unsaved-changes" />} />
                }
                <ContentHeaderView text="Template Details" />
                <FormContainer className="template-details-form small-form-width">
                    <FormRowView text="SEC Code">
                        { ui.showSecCode.isActive && 
                        <b className="form-detail-text sec-code-text">
                            <span>{ template.secCode }</span>
                        </b>
                        }
                    </FormRowView>
                    <FormRowView text="* Template Name">
                        <UITextInput value={ template.name } onChange={ onChangeDataProp("name") } dataPoint="Template_Details_Template_Name" />
                    </FormRowView>
                    <FormRowView text="Company Discretionary Data">
                        <UITextInput value={ template.companyDiscretionaryData } onChange={ onChangeDataProp("companyDiscretionaryData") } dataPoint="Template_Details_Company_Discretionary_Data_Input" />
                    </FormRowView>
                    <FormRowView>
                        <UICheckbox label="Is Restricted" value={ template.isRestricted } onChange={ onChangeDataProp("isRestricted") } dataPoint="Template_Details_Is_Restricted" />
                    </FormRowView>
                    <FormRowView>
                        <UICheckbox label="Offset Individually" value={ template.offsetIndividually } onChange={ onChangeDataProp("offsetIndividually") } dataPoint="Template_Details_Offset_Individually" />
                    </FormRowView>
                    <FormButtonRow>
                        <DisabledPrompt><PrimaryButton text="Deny Specific Users" className="deny-users-button"  dataPoint="Template_Details_Deny_Specific_Users_Button" /></DisabledPrompt>
                    </FormButtonRow>
                    <FormRowView text="* Company Entry Description">
                        <UITextInput value={ template.companyEntryDescription } onChange={ onChangeDataProp("companyEntryDescription") } dataPoint="Template_Details_Company_Entry_Description_Input"/>
                    </FormRowView>
                    <FormRowView text="* Company">
                        <CustomSelect active={ ui.companySelectIsActive.isActive } value={ template.companyAccount ? template.companyAccount.id : ""}
                            onChange={ onChangeDataProp("companyAccount") } dataPoint="Template_Details_Company_Select">
                            { companyAccountItems }
                        </CustomSelect>
                    </FormRowView>
                </FormContainer>
                <ACHTransfersFromSection data={ data } ui={ ui } { ...action } />
                <TemplateDetailsToSection data={ data } ui={ ui } { ...action } showHoldPrenoteDistributionColumns={ true } />
                <div className="import-csv-file-container">
                    <b>Import CSV File</b>
                    <div className="flex-container flex-horizontal align-center button-container">
                        <DisabledPrompt>
                            <SecondaryButton text="Choose File"  dataPoint="Choose_File_Button"/>
                        </DisabledPrompt>
                        <p>No file chosen</p>
                        <DisabledPrompt><PrimaryButton className="disabled-button" text="Import" /></DisabledPrompt>
                    </div>
                </div>
                <div className="top-border flex-container flex-horizontal button-group">
                    <SecondaryButton text="Cancel" onClick={() => { navigate("/achTemplates") }} />
                    <DisabledPrompt><PrimaryButton text="Initiate Prenotes" dataPoint="Initiate_Prenotes_Button" /></DisabledPrompt>
                    <PrimaryButton text="Save" onClick={ onSaveButtonClick }  dataPoint="Save_Button"/>
                    <PrimaryButton text="Save And Close" onClick={ onSaveAndCloseButtonClick } dataPoint="Save_And_Close_Button"/>
                </div>
            </CenterContentContainer>
        </PageContentView>
    );
}
